import { useSearchParams } from 'react-router-dom';

import { LoyaltyClientsTab } from './LoyaltyClientsTab';
import { LoyaltyConfigurationsTab } from './LoyaltyConfigurationsTab';
import { LoyaltyRewardsTab } from './LoyaltyRewardsTab';
import { WalletCardTab } from './wallet/WalletCardTab';
import { PageTour } from '../../../components/ui/PageTour';
import { TourStep } from '../../../components/ui/GuidedTour';

const tabs = [
    { key: 'clients', label: 'Clientes', icon: 'bi-people' },
    { key: 'configurations', label: 'Configuración', icon: 'bi-sliders' },
    { key: 'rewards', label: 'Recompensas', icon: 'bi-gift' },
    { key: 'card', label: 'Tarjeta digital', icon: 'bi-wallet2' },
] as const;

type TabKey = typeof tabs[number]['key'];

// Guía de la pantalla (se abre sola la primera vez; los elementos se marcan con data-tour)
const TOUR_STEPS: TourStep[] = [
    {
        key: 'intro', icon: 'bi-gift', title: 'Programa de lealtad',
        content: 'Premie a sus clientes frecuentes: acumulan puntos con cada compra y los canjean por recompensas. Todo se administra desde las pestañas de esta pantalla.',
    },
    {
        key: 'configurations', target: '[data-tour="loyalty-tab-configurations"]', icon: 'bi-sliders', title: '1. Configuración',
        content: 'Empiece aquí: defina cómo y cuántos puntos acumulan sus clientes con sus compras.',
    },
    {
        key: 'rewards', target: '[data-tour="loyalty-tab-rewards"]', icon: 'bi-gift', title: '2. Recompensas',
        content: 'Cree las recompensas que sus clientes pueden canjear y cuántos puntos cuesta cada una.',
    },
    {
        key: 'card', target: '[data-tour="loyalty-tab-card"]', icon: 'bi-wallet2', title: '3. Tarjeta digital',
        content: 'Diseñe la tarjeta que sus clientes guardan en el celular (Wallet) para ver sus puntos.',
    },
    {
        key: 'clients', target: '[data-tour="loyalty-tab-clients"]', icon: 'bi-people', title: '4. Clientes',
        content: 'Registre a sus clientes, búsquelos y consulte sus puntos acumulados y canjes.',
    },
];

// Mantenimiento del programa de lealtad: clientes, configuración de acumulación, recompensas y tarjeta digital (Wallet).
// La pestaña activa queda en la URL (?tab=...) para poder enlazarla y conservarla al recargar.
export const LoyaltySettingsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get('tab');
    const activeTab: TabKey = tabs.some(t => t.key === requested) ? requested as TabKey : 'clients';

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Programa de lealtad</h3>
                    <p className="text-muted mb-0">Administre los clientes, la forma en que acumulan, las recompensas que pueden canjear y el diseño de su tarjeta digital.</p>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                    <PageTour id="loyalty" steps={TOUR_STEPS} />
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <ul className="nav nav-tabs mb-3">
                        {tabs.map(tab => (
                            <li className="nav-item" key={tab.key} data-tour={`loyalty-tab-${tab.key}`}>
                                <button
                                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setSearchParams({ tab: tab.key }, { replace: true })}
                                >
                                    <i className={`bi ${tab.icon} me-1`} /> {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {activeTab === 'clients' && <LoyaltyClientsTab />}
                    {activeTab === 'configurations' && <LoyaltyConfigurationsTab />}
                    {activeTab === 'rewards' && <LoyaltyRewardsTab />}
                    {activeTab === 'card' && <WalletCardTab />}
                </div>
            </div>

            <div style={{ height: '60px' }}></div>
        </div>
    );
};

const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
};
